import { useCallback } from "react";
import { useTellescopeMutation } from "@tellescope/tanstack-query";
import type { ExternalRedirectTarget } from "@tellescope/react-hooks";
import type { Enduser, FormField, FormFieldOptions } from "@tellescope/types-models";
import { resolveRedirectUrl } from "./utils";
import type { TellescopeFieldResponse } from "./types";

export type RedirectFieldTarget = { destination: "external" } & ExternalRedirectTarget;

export type ContinueToFormInApp = (preparedResponse: PreparedFormResponse) => boolean;

export interface PreparedFormResponse {
  id: string;
  formId: string;
  accessCode?: string;
}

const appendBackUrl = (target: string, backUrl: string | undefined): string => {
  if (!backUrl) return target;

  const parsed = new URL(target);
  parsed.searchParams.set("back", backUrl);
  return parsed.toString();
};

/**
 * The enduser the redirect target should name, resolved as legacy did (`inputs.tsx:4853-4857`): an
 * `eId` query param on the current URL wins, then the id the wizard was mounted with, then the
 * record's own. The param exists so a link can carry the enduser through a chain of public forms
 * where no session identifies them.
 */
const resolveTemplatedEnduserId = (enduserId: string, recordId: string | undefined): string => {
  const fromParam = new URLSearchParams(window.location.search).get("eId");
  return fromParam || enduserId || recordId || "";
};

export interface UseResolveRedirectTargetArgs {
  options: FormFieldOptions | undefined;
  enduser: (Partial<Enduser> & { id?: string }) | undefined;
  enduserId: string;
  responses: TellescopeFieldResponse[];
  fieldsArr: (Pick<FormField, "intakeField"> & { id: string })[];
  submitForm: () => Promise<unknown>;
  isSubmitted: boolean;
  formResponseId?: string;
  rootResponseId?: string;
  documentGroupBackUrl?: string;
  continueToFormInApp?: ContinueToFormInApp;
}

/**
 * Builds the resolver a Redirect field uses to leave the current form: submit it, then produce the
 * destination — either the configured external URL with identity template values substituted, or a
 * freshly prepared response for the form named by `redirectFormId`.
 *
 * `redirectExternalUrl` takes priority when both options are configured, and the target form's
 * response is only prepared when that branch actually wins, since preparing one creates a record.
 *
 * Resolves to `undefined` when the configured URL is not http(s), leaving the enduser on the form
 * rather than navigating somewhere unsafe.
 *
 * Submission is skipped when the form is already submitted, and is otherwise best-effort: a failure
 * still redirects, so a save error cannot strand the enduser on a page whose only purpose is to
 * leave.
 *
 * A chained form's response is prepared with lineage back to this one, so a document group's chain
 * stays walkable server-side.
 */
export const useResolveRedirectTarget = ({
  options,
  enduser,
  enduserId,
  responses,
  fieldsArr,
  submitForm,
  isSubmitted,
  formResponseId,
  rootResponseId,
  documentGroupBackUrl,
  continueToFormInApp,
}: UseResolveRedirectTargetArgs): (() => Promise<RedirectFieldTarget | undefined>) | undefined => {
  const { mutateAsync: prepareFormResponse } = useTellescopeMutation({
    model: "form_responses",
    method: "prepare_form_response",
  });

  const redirectFormId = options?.redirectFormId;
  const externalUrl = options?.redirectExternalUrl;

  return useCallback(async (): Promise<RedirectFieldTarget | undefined> => {
    if (!redirectFormId && !externalUrl) return undefined;

    const formTarget =
      redirectFormId && !externalUrl
        ? await prepareFormResponse({
            formId: redirectFormId,
            enduserId,
            ...(formResponseId ? { parentResponseId: formResponseId } : {}),
            ...(rootResponseId || formResponseId ? { rootResponseId: rootResponseId || formResponseId } : {}),
          })
        : undefined;

    if (!isSubmitted) {
      try {
        await submitForm();
      } catch {
        // Best-effort - matches T1/P1
      }
    }

    if (externalUrl) {
      const url = resolveRedirectUrl({
        externalUrl,
        enduser: { ...enduser, id: resolveTemplatedEnduserId(enduserId, enduser?.id) },
        responses,
        fieldsMap: fieldsArr.reduce<Record<string, Pick<FormField, "intakeField">>>((acc, f) => {
          acc[f.id] = f;
          return acc;
        }, {}),
        currentParams: new URLSearchParams(window.location.search),
      });

      return url ? { destination: "external", url } : undefined;
    }

    if (!formTarget) return undefined;

    const continued = continueToFormInApp?.({
      id: formTarget.response.id,
      formId: formTarget.response.formId,
      accessCode: formTarget.accessCode ?? formTarget.response.accessCode,
    });
    if (continued) return undefined;

    return {
      destination: "external",
      url: appendBackUrl(formTarget.fullURL, documentGroupBackUrl),
      replaceHistoryEntry: true,
    };
  }, [
    redirectFormId,
    externalUrl,
    prepareFormResponse,
    enduserId,
    submitForm,
    isSubmitted,
    formResponseId,
    rootResponseId,
    documentGroupBackUrl,
    continueToFormInApp,
    enduser,
    responses,
    fieldsArr,
  ]);
};
